import { useLiveQuery } from "dexie-react-hooks";
import { offlineDb } from "../db/offlineDb";
import { effectiveTableId } from "../lib/captainMesh/db";

/**
 * Returns the materialized local projection for a specific aggregate (order).
 * The UI subscribes directly to IndexedDB. As events flow in (from WebRTC or local action),
 * the materializer updates Dexie, and this hook triggers a React re-render automatically.
 */
export function useCaptainOperationalState(aggregateId) {
  const materializedRecord = useLiveQuery(
    () => aggregateId ? offlineDb.captain_materialized.get(aggregateId) : null,
    [aggregateId]
  );

  if (!materializedRecord) return null;
  return {
    ...materializedRecord.state,
    order_id: materializedRecord.state.id // Canonical normalized mapping
  };
}

// Statuses that hold a table, mirrored from lib/occupancy. A server-side
// "closed"/"cancelled"/"rejected" recorded on the mapping row outranks the
// local fold — the capture never learns terminal states from its own events.
const LIVE = new Set(["open", "kot_sent", "billing"]);

/**
 * Returns all active tables from the materialized state,
 * simulating the FastAPI /api/tables endpoint.
 */
export function useCaptainAllTablesState() {
  const allMaterialized = useLiveQuery(
    () => offlineDb.captain_materialized.toArray(),
    []
  );
  const mappings = useLiveQuery(
    () => offlineDb.mesh_order_map.toArray(),
    []
  );

  const mapByAggregate = new Map((mappings || []).map(m => [m.aggregate_id, m]));

  const states = allMaterialized ? allMaterialized.map(m => {
    const mapping = mapByAggregate.get(m.aggregate_id);
    const lines = Object.values(m.state.items || {});
    // A line the server acknowledged a KOT for means the order is genuinely in
    // the kitchen — the local fold's status stays "open" forever otherwise, and
    // every downstream gate (labels, manager-authorised cancel) reads status.
    const derivedStatus =
      mapping?.order_status === "billing" || (mapping?.order_status && !LIVE.has(mapping.order_status))
        ? mapping.order_status
        : lines.some(l => l.kot_acked)
          ? "kot_sent"
          : m.state.status;
    const activeLines = lines.filter(i => i.status !== "cancelled" && i.status !== "removed");
    const fallbackAmount = activeLines.reduce(
      // The fold stores `unit_price`; reading `price` made every local total 0.
      (sum, item) => sum + (Number(item.unit_price ?? item.price) || 0) * (Number(item.quantity) || 0),
      0
    );
    const createdAtMs = m.state.created_at ? new Date(m.state.created_at).getTime() : null;
    const fallbackElapsed = createdAtMs ? Math.max(0, Math.floor((Date.now() - createdAtMs) / 1000)) : 0;

    return {
      amount: fallbackAmount,
      elapsed_seconds: fallbackElapsed,
      ...m.state,
      // The fold sets state.id from ORDER_STARTED. An aggregate this pad holds
      // without that event (replicated or adopted) had no id, so every ADD went
      // to Table.get(undefined) and threw "Invalid argument to Table.get()" with
      // nothing on screen (C05, table 128, 2026-10-14). The row's own key is the
      // aggregate id by construction.
      order_id: m.state.id ?? m.aggregate_id, // Canonical normalized mapping
      // The server's table when it has told us (a Move Table on any terminal),
      // else the table this capture started on.
      table_id: effectiveTableId(m, mapping),
      status: derivedStatus,
      capture_status: m.state.status,
      server_order_id: mapping?.order_id ?? null,
      order_number: mapping?.order_number ?? m.state.order_number ?? null,
    };
  }) : [];

  // Keyed on the table's DB id — the one thing the mesh events actually carry
  // (ORDER_STARTED stores payload.table_id = table.id; the reducer never sets a
  // table_number). This used to accept table.number and match on either field,
  // which worked only in the test seed where ids and numbers happen to be equal
  // (tables 1-10). On live data (id=11 → number=25 "25 Gardenn") the lookup
  // matched nothing, so with the server down every table rendered vacant — in
  // exactly the scenario Captain Mesh exists for.
  const orderForTable = (tableDbId) => {
    // Live orders only. Without the filter a closed, cancelled or rejected
    // aggregate held its table occupied on this device forever — there is no
    // event that ends a mesh order locally; the ending is a server fact carried
    // on the mapping row.
    //
    // Newest capture first. Two live-looking aggregates on one table means one
    // is an earlier party whose ending this pad has not heard yet; `find` in
    // insertion order returned the OLDEST, which put a finished order's lines
    // and KOTs back on the table (Mahendra, Garden 2, 2026-10-23).
    //
    // Nor a printed bill: printing frees the table for the next party (bible
    // §39.2) and the bill lives on its own card. Returned here, every tap on the
    // table reopened the billed order, where nothing can be added, so no captain
    // could punch for the new guests until the old bill was settled (owner,
    // 2026-10-24).
    const live = states.filter(s => s.table_id === tableDbId && s.status !== "billing" && (LIVE.has(s.status) || s.status === "CONFLICTED"));
    live.sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
    return live[0] || null;
  };

  // Both live queries start undefined: until they land, "no order on this table"
  // means "not read yet", and a guard acting on it would misroute a busy table.
  const loaded = allMaterialized !== undefined && mappings !== undefined;

  return { states, orderForTable, loaded };
}
